import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Save } from "lucide-react";
import NumberInput from "../../components/ui/NumberInput";

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
    {children}
    {hint && <div className="text-[11px] text-gray-400 mt-1">{hint}</div>}
  </div>
);

export default function CreateAgent() {
  const navigate = useNavigate();
  const [districts, setDistricts] = useState([]);
  const [parents, setParents] = useState([]);
  const [form, setForm] = useState({
    username: "",
    password: "",
    name: "",
    email: "",
    mobile: "",
    city: "",
    state: "",
    deposit_commission_pct: 0,
    withdraw_commission_pct: 0,
    balance: 0,
    status: "active",
    parent_agent_id: "",
    parent_deposit_override_pct: 0,
    parent_withdraw_override_pct: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/bd-districts").then((r) => setDistricts(r.data?.items || [])).catch(() => setDistricts([]));
    api.get("/agents").then((r) => {
      // only main agents (no parent) are eligible as parents for a sub-agent
      setParents((r.data || []).filter((a) => !a.parent_agent_id));
    }).catch(() => setParents([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if ((form.username || "").length < 3) return toast.error("Username must be 3+ characters");
    if ((form.password || "").length < 6) return toast.error("Password must be 6+ characters");
    if (!form.city) return toast.error("Select a City (Bangladesh district)");
    if (!form.state.trim()) return toast.error("Enter a unique Agent State (address)");
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.parent_agent_id) delete payload.parent_agent_id;
      await api.post("/agents", payload);
      toast.success(`${form.parent_agent_id ? "Sub-agent" : "Agent"} created — they can log in at /agent/login as ${form.username}`);
      navigate("/root/admin/agents/list");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to create agent";
      toast.error(typeof msg === "string" ? msg : "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="create-agent-page">
      <PageHeader title="Create Agent" subtitle="Onboard a new partner agent — they will get a login for the Agent Panel at /agent/login" />
      <form onSubmit={submit} className="bg-white border border-gray-200 rounded-sm p-6 max-w-3xl" data-testid="create-agent-form">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Login Credentials</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <Field label="Agent Username" hint="3-32 chars, used for /agent/login">
            <input required minLength={3} maxLength={32} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="create-agent-username" />
          </Field>
          <Field label="Password" hint="Min 6 characters — share with the agent securely">
            <input required minLength={6} type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="create-agent-password" />
          </Field>
        </div>

        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Personal Info</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <Field label="Full Name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="create-agent-name" />
          </Field>
          <Field label="Email">
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="create-agent-email" />
          </Field>
          <Field label="Mobile">
            <input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="create-agent-mobile" />
          </Field>
        </div>

        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Withdraw Address (shown to users)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <Field label="Agent City" hint="One of the 64 Bangladesh districts">
            <select required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="create-agent-city">
              <option value="">— Select district —</option>
              {districts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Agent State (Unique)" hint="Specific address shown as the withdraw target — e.g. 'Mirpur — Section 10, Shop 22'">
            <input required maxLength={80} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="create-agent-state" />
          </Field>
        </div>

        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Commissions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <Field label="Deposit Commission %" hint="% of every agent → user deposit credited back to the agent's balance">
            <NumberInput required min="0" max="100" value={form.deposit_commission_pct} onChange={(n) => setForm({ ...form, deposit_commission_pct: n })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="create-agent-deposit-pct" />
          </Field>
          <Field label="Withdraw Commission %" hint="% of every approved withdrawal credited to the agent's balance">
            <NumberInput required min="0" max="100" value={form.withdraw_commission_pct} onChange={(n) => setForm({ ...form, withdraw_commission_pct: n })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="create-agent-withdraw-pct" />
          </Field>
        </div>

        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Wallet & Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Initial Balance (৳ BDT)" hint="Funds the agent can use to deposit to users">
            <NumberInput required min="0" value={form.balance} onChange={(n) => setForm({ ...form, balance: n })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="create-agent-balance" />
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="create-agent-status">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </Field>
        </div>

        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 mt-5">Hierarchy (optional)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Parent Agent" hint="Leave empty for a top-level agent. Pick a parent to create a sub-agent reporting to them.">
            <select value={form.parent_agent_id} onChange={(e) => setForm({ ...form, parent_agent_id: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="create-agent-parent">
              <option value="">— No parent (top-level agent) —</option>
              {parents.map((p) => (
                <option key={p.id} value={p.id}>{p.username} · {p.name} {p.city ? `(${p.city})` : ""}</option>
              ))}
            </select>
          </Field>
        </div>
        {form.parent_agent_id && (
          <div className="mt-4 p-4 border border-purple-200 bg-purple-50/40 rounded-sm" data-testid="create-agent-parent-override-row">
            <div className="text-xs font-bold uppercase tracking-wider text-purple-700 mb-3">Parent Override Commission</div>
            <p className="text-[11px] text-purple-700/80 mb-3 leading-relaxed">
              When this sub-agent makes a deposit / approves a withdrawal, the <strong>parent agent</strong> will receive a separate commission credited automatically (in addition to the sub-agent's own commission above). Set both to <code>0</code> to disable parent payouts.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Parent · Deposit Override %" hint="% of every deposit volume credited to the parent agent on top of the sub-agent's own commission.">
                <NumberInput min="0" max="100" value={form.parent_deposit_override_pct} onChange={(n) => setForm({ ...form, parent_deposit_override_pct: n })} className="w-full h-10 px-3 border border-purple-200 rounded-sm text-sm tabular bg-white" data-testid="create-agent-parent-deposit-override" />
              </Field>
              <Field label="Parent · Withdraw Override %" hint="% of every approved withdrawal volume credited to the parent agent on top of the sub-agent's own commission.">
                <NumberInput min="0" max="100" value={form.parent_withdraw_override_pct} onChange={(n) => setForm({ ...form, parent_withdraw_override_pct: n })} className="w-full h-10 px-3 border border-purple-200 rounded-sm text-sm tabular bg-white" data-testid="create-agent-parent-withdraw-override" />
              </Field>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6 pt-5 border-t border-gray-100">
          <button type="button" onClick={() => navigate("/root/admin/agents/list")} className="h-10 px-4 border border-gray-200 rounded-sm text-sm">Cancel</button>
          <button type="submit" disabled={loading} className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="create-agent-submit">
            <Save size={14} /> {loading ? "Saving..." : "Create Agent"}
          </button>
        </div>
      </form>
    </div>
  );
}
